/**
 * GP-1046 — auxiliary ("warning") label layout for the vial label.
 *
 * The aux strip is a column of AUX_STICKER_COUNT stacked peel-off stickers; each
 * sticker is one physical label that gets stuck on the vial, so one warning has
 * to fit inside ONE sticker — completely.
 *
 * The dispensary focus group reported three layout defects, all traceable to a
 * FIXED font size:
 *  - "cut off" — a warning longer than the fixed font's character budget was
 *    truncated with an ellipsis mid-sentence. Measured against production FDB
 *    data (2026-08-04): warning text averages 76 chars and reaches 181, and
 *    13,679 of 35,344 warning texts exceed the ~90 chars the old fixed 22-dot
 *    font could hold. So ~39 % of printed warnings were being clipped.
 *  - "bleeding into each other" — text sat 4 dots off the top of its sticker
 *    with no bottom gutter, so ink landed on the perforation between stickers
 *    and read as part of the neighbouring label.
 *  - short warnings printed needlessly small, because the size was chosen for
 *    the worst case rather than for the text at hand.
 *
 * Fix: pick the LARGEST font from a ladder at which the whole warning wraps
 * inside its sticker, then centre the wrapped block so both gutters are equal.
 *
 * Unit-agnostic on purpose: the ZPL path measures in printer dots and the
 * browser-print fallback in points, and both need the same decision, so the
 * caller supplies geometry and a font ladder in ITS OWN unit and gets a font
 * size back in that unit. Ratios describe the font metrics of the target
 * renderer (see the two AUX_*_METRICS constants below).
 */

/** The aux strip is 5 stacked stickers — one warning each. */
export const AUX_STICKER_COUNT = 5;

/**
 * ZPL font ladder, in dots at 203 dpi. Starts ABOVE the old fixed 22 so a short
 * warning ("Take with food") now prints bigger, and reaches down to 12 so the
 * 181-char worst case still fits without truncation.
 */
export const AUX_ZPL_FONT_STEPS = [26, 24, 22, 20, 18, 16, 14, 12];

/**
 * ZPL font 0 (^A0) is proportional, but the width parameter is its MAXIMUM
 * character advance — dividing the strip width by the full font width is the
 * conservative choice, so even a line of all-wide glyphs cannot overflow.
 */
export const AUX_ZPL_METRICS = { charWidthRatio: 1, lineHeightRatio: 1.05 };

/**
 * Browser-print fallback ladder, in points (the aux column is styled in pt).
 *
 * The bottom two steps look uncomfortably small, and they are — the browser
 * label reserves only a 1.5in-wide strip where the ZPL layout gives the strip
 * ~82mm, so the same warning has roughly half the writing width here. Without
 * them a 150+ char FDB warning (the longest is 181) could not fit at all and
 * would be clipped mid-sentence, which is the defect being fixed: on a warning
 * label, complete-but-small beats half a sentence. The Zebra/ZPL path is the
 * production printer and never needs these steps.
 */
export const AUX_HTML_FONT_STEPS = [9, 8.5, 8, 7.5, 7, 6.5, 6, 5.5, 5, 4.5, 4];

/**
 * Arial Bold averages ~0.55em per character; 0.62 leaves headroom for
 * wide-glyph runs without shrinking every label a step too far.
 */
export const AUX_HTML_METRICS = { charWidthRatio: 0.62, lineHeightRatio: 1.15 };

/**
 * Writing area of ONE sticker in the browser-print label, in points. Derived
 * from the .auxiliary-space / .aux-warning CSS in ThermalLabelPrint.tsx:
 * a 1.5in strip less 0.06in of side padding ≈ 1.38in ≈ 99pt wide, and
 * (2.1in - 0.08in padding) / 5 stickers ≈ 0.4in ≈ 29pt tall, less the ~1.5pt
 * gutter at each end that keeps ink off the perforation between stickers.
 * Keep this in step with that CSS — it is the one place both the print path and
 * the printer-test preview measure against.
 */
export const AUX_HTML_STICKER = { usableWidth: 99, usableHeight: 26 };

export interface AuxFontMetrics {
  /** Width of one character as a fraction of the font size. */
  charWidthRatio: number;
  /** Line-to-line advance as a fraction of the font size. */
  lineHeightRatio: number;
}

export interface AuxFitRequest extends AuxFontMetrics {
  /** Writing width available on one sticker, in the caller's unit. */
  usableWidth: number;
  /** Writing height available on one sticker (gutters already deducted). */
  usableHeight: number;
  /** Candidate font sizes, largest first, in the caller's unit. */
  fontSteps: number[];
}

export interface AuxFit {
  /** Chosen font size, in the caller's unit. */
  font: number;
  /** Line-to-line advance for the chosen font, in the caller's unit. */
  lineHeight: number;
  /** The warning, wrapped to the chosen font's line budget. */
  lines: string[];
  /**
   * false when even the smallest font could not hold the whole text and `lines`
   * had to be clamped to the sticker. Callers can surface this; with the
   * shipped ladders and real FDB data it does not occur.
   */
  fits: boolean;
}

/**
 * Greedy word wrap with NO truncation. A single word longer than the line
 * budget is hard-split rather than allowed to run past the sticker edge.
 */
export function wrapAuxText(text: string, maxCharsPerLine: number): string[] {
  const budget = Math.max(1, Math.floor(maxCharsPerLine));
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    if (current && current.length + 1 + word.length <= budget) {
      current += ` ${word}`;
      continue;
    }
    if (current) lines.push(current);
    current = word;
    // A word that cannot fit a line on its own is split across lines.
    while (current.length > budget) {
      lines.push(current.slice(0, budget));
      current = current.slice(budget);
    }
  }
  if (current) lines.push(current);

  return lines;
}

/**
 * Pick the largest font at which `text` wraps entirely inside one sticker.
 */
export function fitAuxWarning(text: string, req: AuxFitRequest): AuxFit {
  const steps = req.fontSteps.length > 0 ? req.fontSteps : [1];
  let fallback: AuxFit | null = null;

  for (const font of steps) {
    const lineHeight = font * req.lineHeightRatio;
    const charsPerLine = Math.floor(req.usableWidth / (font * req.charWidthRatio));
    const maxLines = Math.max(1, Math.floor(req.usableHeight / lineHeight));
    const lines = wrapAuxText(text, charsPerLine);

    if (lines.length <= maxLines) {
      return { font, lineHeight, lines, fits: true };
    }
    // Remember the smallest step so an unfittable warning still prints as much
    // as the sticker holds instead of spilling onto the neighbouring label.
    fallback = { font, lineHeight, lines: lines.slice(0, maxLines), fits: false };
  }

  return fallback ?? { font: steps[steps.length - 1], lineHeight: 0, lines: [], fits: true };
}

/**
 * Fit one warning to a browser-print sticker (points). Used by both the print
 * path and the printer-test preview so what staff see is what gets printed.
 */
export function fitAuxWarningForHtml(text: string): AuxFit {
  return fitAuxWarning(text, {
    ...AUX_HTML_STICKER,
    fontSteps: AUX_HTML_FONT_STEPS,
    ...AUX_HTML_METRICS,
  });
}
